import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Button,
  Chip,
  alpha,
  useTheme,
  CircularProgress,
} from "@mui/material";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Building2,
  Users,
  FileText,
} from "lucide-react";
import {
  fetchMarketingOrgs,
  deleteMarketingOrg,
} from "../../../../../redux/actions/facturationAction";
import DeleteConfirmDialog from "../../Marketing/components/DeleteConfirmDialog";
import { enqueueSnackbar } from "notistack";

const ClientList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const { marketingOrgs, loading } = useSelector((state) => state.facturation);
  const [search, setSearch] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [orgToDelete, setOrgToDelete] = useState(null);

  useEffect(() => {
    dispatch(fetchMarketingOrgs(search));
  }, [dispatch, search]);

  const handleDelete = (e, org) => {
    e.stopPropagation();
    setOrgToDelete(org);
    setDeleteDialogOpen(true);
  };

  return (
    <Box>
      {/* Toolbar */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 2 }}>
        <TextField
          size="small"
          placeholder="Rechercher par nom..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={16} />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 280 }}
        />
        <Button
          variant="contained"
          size="small"
          startIcon={<Plus size={16} />}
          onClick={() => navigate("/admin/facturation/clients/new")}
          sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 600 }}
        >
          Nouvelle organisation
        </Button>
      </Box>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: "12px" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", color: theme.palette.text.secondary }}>
                  Organisation
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", color: theme.palette.text.secondary }}>
                  ICE
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", color: theme.palette.text.secondary }}>
                  Ville
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", color: theme.palette.text.secondary }}>
                  Contacts
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", color: theme.palette.text.secondary }}>
                  Devis
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", color: theme.palette.text.secondary }}>
                  Date
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", color: theme.palette.text.secondary }} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {marketingOrgs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                      <Building2 size={40} strokeWidth={1} color={theme.palette.text.disabled} />
                      <Typography variant="body2" color="text.secondary">
                        Aucune organisation trouvée
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Plus size={14} />}
                        onClick={() => navigate("/admin/facturation/clients/new")}
                        sx={{ mt: 1, textTransform: "none" }}
                      >
                        Créer une organisation
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                marketingOrgs.map((org) => (
                  <TableRow
                    key={org.id}
                    hover
                    sx={{ cursor: "pointer", "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.04) } }}
                    onClick={() => navigate(`/admin/facturation/clients/${org.id}`)}
                  >
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: "8px",
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Building2 size={16} color={theme.palette.primary.main} />
                        </Box>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {org.name}
                          </Typography>
                          {org.phone && (
                            <Typography variant="caption" color="text.secondary">
                              {org.phone}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                        {org.ice || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{org.city || "—"}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={<Users size={12} />}
                        label={org.contact_count || 0}
                        size="small"
                        sx={{ fontSize: "0.75rem", height: 22 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={<FileText size={12} />}
                        label={org.quote_count || 0}
                        size="small"
                        sx={{ fontSize: "0.75rem", height: 22 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {org.created_at
                          ? new Date(org.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })
                          : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                        <Tooltip title="Modifier">
                          <IconButton size="small" onClick={() => navigate(`/admin/facturation/clients/${org.id}`)}>
                            <Edit size={15} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Supprimer">
                          <IconButton size="small" onClick={(e) => handleDelete(e, org)} sx={{ color: "error.main" }}>
                            <Trash2 size={15} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete Confirm Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => { setDeleteDialogOpen(false); setOrgToDelete(null); }}
        onConfirm={async () => {
          if (!orgToDelete) return;
          await dispatch(deleteMarketingOrg(orgToDelete.id));
          enqueueSnackbar("Organisation supprimée", { variant: "success" });
          setDeleteDialogOpen(false);
          setOrgToDelete(null);
        }}
        itemName={orgToDelete?.name || ""}
        itemType="organisation"
      />
    </Box>
  );
};

export default ClientList;
